import type { ProductSelectDto } from '@@types/api.types';
import { SankeyModal } from '@components/shared/sankey-modal.component';
import { Tile } from '@components/shared/tile.component';
import { useGetProducts } from '@queries/product.query';
import type { SankeyChartConfig } from '@utils/sankey.util';
import { SankeyDataBuilder as Builder, createSankeyChartUtils } from '@utils/sankey.util';
import { ZoomInIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import React from 'react';
import { EmptyStateContainerProps } from './empty-state-container.component';
import type { SankeyData, SankeyProps, SankeySettings } from './sankey.component';
import { Sankey } from './sankey.component';

// =============================================================================
// TYPES
// =============================================================================

type NodeType = 'product' | 'category' | 'subcategory' | 'technology';

type NodeContext = {
  productId?: string;
  technologyId?: string;
  type: NodeType;
};

export type ProductsSankeyChartProps = {
  products: ProductSelectDto[];
  /** Optional product ID to filter the visualization */
  productId?: string;
  settings?: Partial<SankeySettings>;
  sankeyProps?: Partial<SankeyProps>;
};

// =============================================================================
// CONFIGURATION
// =============================================================================

const PRODUCTS_SANKEY_CONFIG: SankeyChartConfig<NodeType> = {
  nodeTypes: {
    product: {
      color: '#3B82F6', // Blue
      label: 'Product',
    },
    category: {
      color: '#10B981', // Emerald
      label: 'Category',
    },
    subcategory: {
      color: '#F59E0B', // Amber
      label: 'Sub-category',
    },
    technology: {
      color: '#8B5CF6', // Violet
      label: 'Technology',
    },
  },
};

const chartUtils = createSankeyChartUtils(PRODUCTS_SANKEY_CONFIG);

// =============================================================================
// DATA PROCESSING
// =============================================================================

function processProductsData(
  products: ProductSelectDto[],
  productId?: string,
): SankeyData<Record<string, unknown>, NodeContext> {
  const builder = new Builder<NodeContext>();

  if (!products.length) return builder.getData();

  products
    .filter((p) => !productId || p.id === productId)
    .forEach((product) => {
      // Always add product node
      builder.addNode({
        name: product.name,
        type: 'product',
        extraData: { type: 'product', productId: product.id },
      });

      const technologies = product.technologies || [];

      technologies.forEach((tech) => {
        // Category node
        if (tech.category) {
          builder.addNode({ name: tech.category.name, type: 'category', extraData: { type: 'category' } });
          builder.addLink({
            sourceName: product.name,
            sourceType: 'product',
            targetName: tech.category.name,
            targetType: 'category',
            value: 1,
          });
        }

        // Technology node
        builder.addNode({
          name: tech.name,
          type: 'technology',
          extraData: { type: 'technology', technologyId: tech.id },
        });

        // Handle subcategory logic only if category exists
        if (tech.category && tech.subcategory) {
          // Sub-category node
          const subcategoryName = tech.subcategory.name;
          builder.addNode({ name: subcategoryName, type: 'subcategory', extraData: { type: 'subcategory' } });

          // Link category -> subcategory
          builder.addLink({
            sourceName: tech.category.name,
            sourceType: 'category',
            targetName: subcategoryName,
            targetType: 'subcategory',
            value: 1,
          });

          // Link subcategory -> technology
          builder.addLink({
            sourceName: subcategoryName,
            sourceType: 'subcategory',
            targetName: tech.name,
            targetType: 'technology',
            value: 1,
          });
        } else if (tech.category) {
          // No subcategory, link category directly to technology
          builder.addLink({
            sourceName: tech.category.name,
            sourceType: 'category',
            targetName: tech.name,
            targetType: 'technology',
            value: 1,
          });
        } else {
          // No category, link product directly to technology
          builder.addLink({
            sourceName: product.name,
            sourceType: 'product',
            targetName: tech.name,
            targetType: 'technology',
            value: 1,
          });
        }
      });
    });

  return builder.getData();
}

// =============================================================================
// CHART COMPONENT
// =============================================================================

const ProductsSankeyChart: React.FC<ProductsSankeyChartProps> = (props) => {
  const { products, productId, settings, sankeyProps } = props;

  const sankeyData = useMemo(() => processProductsData(products, productId), [products, productId]);

  if (!sankeyData.nodes.length || !sankeyData.links.length) {
    console.error('No data to display in Product Portfolio');
    return null;
  }

  return (
    <Sankey
      data={sankeyData}
      formatNodeLabel={chartUtils.formatNodeLabel}
      formatNodeSubtext={chartUtils.formatNodeSubtext}
      settings={{ colorMap: chartUtils.getColorMap(), height: 'auto', ...settings }}
      {...sankeyProps}
    />
  );
};

export const ProductsSankey = React.memo(ProductsSankeyChart);

// =============================================================================
// TILE COMPONENT
// =============================================================================

type ProductsSankeyTileProps = {
  productId?: string;
  title?: string;
  emptyState?: EmptyStateContainerProps;
  settings?: Partial<SankeySettings>;
  sankeyProps?: Partial<SankeyProps>;
};

export const ProductsSankeyTile = (props: ProductsSankeyTileProps) => {
  const {
    emptyState = { title: 'No Product Technologies' },
    productId,
    settings,
    sankeyProps,
    title = 'Product Portfolio',
  } = props;

  const { data: products = [], isLoading } = useGetProducts();

  const filteredProducts = useMemo(() => {
    return products.filter((p) => p.technologies.length > 0 && (productId ? p.id === productId : true));
  }, [products, productId]);

  // Build product options for full-screen modal (only when not filtered to a single technology)
  const productOptions = useMemo(() => {
    if (!filteredProducts.length) return [];

    const unique: Record<string, string> = {};

    filteredProducts.forEach((p) => {
      if (!unique[p.id]) unique[p.id] = p.name;
    });

    return Object.entries(unique).map(([id, name]) => ({ id, name }));
  }, [filteredProducts]);

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);

  const isEmpty = !filteredProducts || filteredProducts.length === 0;

  function openModal() {
    setIsOpenFullScreenModal(true);
  }

  function closeModal() {
    setIsOpenFullScreenModal(false);
  }

  return (
    <>
      <Tile
        className="min-h-[260px]"
        header={{
          title,
          iconButton: {
            icon: <ZoomInIcon />,
            onClick: openModal,
          },
        }}
        emptyState={emptyState}
        hoverReveal
        isEmpty={isEmpty}
        isLoading={isLoading}
      >
        {!isEmpty && (
          <ProductsSankey
            products={filteredProducts}
            settings={settings}
            sankeyProps={{ ...sankeyProps, onOpenFullScreen: openModal }}
          />
        )}
      </Tile>

      {isOpenFullScreenModal && (
        <SankeyModal
          isOpen={isOpenFullScreenModal}
          onClose={closeModal}
          title={title}
          productOptions={productOptions}
        >
          {({ settings: modalSettings, selectedProductId }) => (
            <ProductsSankey
              products={filteredProducts}
              productId={selectedProductId}
              settings={modalSettings}
            />
          )}
        </SankeyModal>
      )}
    </>
  );
};
